import React, { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useLocation } from 'react-router-dom';
import { X, ChevronLeft, ChevronRight } from 'lucide-react';
import { PAGE_TOURS, tourKeyForPath } from './tourSteps';

const NAVY = '#0d1b3e';
const GOLD = '#c9a227';

// Per-page tour controller. Replaces the old global Tour.jsx + MobileTour.jsx.
//
// Two surfaces:
//  1. A subtle "?" button in the bottom-right corner of the viewport (mobile +
//     desktop), always available on tour-supported pages, never gets in the
//     way of content.
//  2. The actual tour overlay, auto-runs once per page per user (tracked in
//     localStorage), then only triggered manually via the "?" button.
//
// What changed from the old tour:
//  - Tours are scoped to the current page. A step's target selector is only
//    looked for on this page, so we can't dangle tooltips next to invisible
//    buttons. If the target somehow isn't found (e.g. due to a UI change),
//    the step is auto-skipped instead of centering with no arrow.
//  - No more cross-page navigation; if you leave the page mid-tour it closes.
//  - Mobile gets the same component, no separate MobileTour with its own
//    "watch the bottom nav pulse" choreography.

function getStorageKey(base) {
  try {
    const token = localStorage.getItem('token');
    if (token) {
      const payload = JSON.parse(atob(token.split('.')[1]));
      return `${base}-${payload.userId || payload.id || ''}`;
    }
  } catch {}
  return base;
}

function measureTarget(selector) {
  if (!selector) return null;
  const el = document.querySelector(selector);
  if (!el) return null;
  const r = el.getBoundingClientRect();
  if (r.width === 0 && r.height === 0) return null;
  return { top: r.top, left: r.left, width: r.width, height: r.height, bottom: r.bottom, right: r.right };
}

function computeTooltipPos(rect, tipW = 340, tipH = 180, gap = 14) {
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  if (!rect) {
    return { top: Math.max(16, (vh - tipH) / 2), left: Math.max(16, (vw - tipW) / 2), arrow: null };
  }
  const spaceBottom = vh - rect.bottom;
  const spaceTop = rect.top;
  const spaceRight = vw - rect.right;

  if (spaceBottom >= tipH + gap + 16) {
    const left = Math.min(Math.max(16, rect.left + rect.width / 2 - tipW / 2), vw - tipW - 16);
    return { top: rect.bottom + gap, left, arrow: 'top', arrowLeft: rect.left + rect.width / 2 - left };
  }
  if (spaceTop >= tipH + gap + 16) {
    const left = Math.min(Math.max(16, rect.left + rect.width / 2 - tipW / 2), vw - tipW - 16);
    return { top: rect.top - tipH - gap, left, arrow: 'bottom', arrowLeft: rect.left + rect.width / 2 - left };
  }
  if (spaceRight >= tipW + gap + 16) {
    const top = Math.min(Math.max(16, rect.top + rect.height / 2 - tipH / 2), vh - tipH - 16);
    return { top, left: rect.right + gap, arrow: 'left', arrowTop: rect.top + rect.height / 2 - top };
  }
  const top = Math.min(Math.max(16, rect.top + rect.height / 2 - tipH / 2), vh - tipH - 16);
  return { top, left: Math.max(16, rect.left - tipW - gap), arrow: 'right', arrowTop: rect.top + rect.height / 2 - top };
}

export default function PageTour() {
  const location = useLocation();
  // Pass the query too, Sales Channels is /settings?tab=integrations and needs
  // its own tour, distinct from the plain /settings one.
  const tourKey = tourKeyForPath(location.pathname + location.search);
  const steps = (tourKey && PAGE_TOURS[tourKey]) || null;

  const [visible, setVisible] = useState(false);
  const [stepIdx, setStepIdx] = useState(0);
  // When set, the tour shows ONLY this step (the AI assistant's "Show me how"
  // fires a single spotlight). null = normal full walkthrough.
  const [soloStepId, setSoloStepId] = useState(null);
  const [rect, setRect] = useState(null);
  const [tipW, setTipW] = useState(340);
  const [tipH, setTipH] = useState(180);
  const tipRef = useRef(null);
  const measureTimer = useRef(null);

  // Filter out steps whose target isn't in the DOM right now, so we never
  // dangle "click this button" copy next to an invisible/non-existent button.
  const visibleSteps = (() => {
    if (!steps) return [];
    // Keep a step if it has no target (centered), its target is on screen now,
    // OR it drives the UI first (clickBefore) to reveal a target that isn't yet
    // visible, e.g. clicking a tab to show that section.
    return steps.filter(s => !s.target || s.clickBefore || measureTarget(s.target));
  })();

  // Auto-run the tour the first time this user lands on this page (per-user
  // per-page localStorage flag). After that, only manual trigger via the "?"
  // button. Disable on routes that have no tour.
  useEffect(() => {
    if (!tourKey) return;
    try {
      const flag = getStorageKey(`scn-pagetour-seen-${tourKey}`);
      if (!localStorage.getItem(flag)) {
        setStepIdx(0);
        setVisible(true);
        localStorage.setItem(flag, '1');
      }
    } catch {}
    // Reset visibility when navigating between pages
    return () => setVisible(false);
  }, [tourKey]);

  // In solo mode (assistant "Show me how") the tour is just the one step.
  const activeSteps = soloStepId ? visibleSteps.filter(s => s.id === soloStepId) : visibleSteps;
  const step = activeSteps[stepIdx];
  const total = activeSteps.length;
  const isLast = stepIdx >= total - 1;
  const isFirst = stepIdx === 0;

  const remeasure = useCallback(() => {
    if (!step) return;
    setRect(measureTarget(step.target));
    if (step.target) {
      const el = document.querySelector(step.target);
      if (el) {
        const r = el.getBoundingClientRect();
        const needsScroll = r.top < 80 || r.bottom > window.innerHeight - 40;
        if (needsScroll) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }
  }, [step]);

  useEffect(() => {
    if (!visible || !step) return;
    // A step can drive the UI before we point at anything, e.g. click a tab so
    // the section (and the element we're about to spotlight) is actually on
    // screen. The retry loop below then waits for the revealed target to render.
    if (step.clickBefore) {
      try { document.querySelector(step.clickBefore)?.click(); } catch { /* no-op */ }
    }
    if (!step.target) { remeasure(); return; }
    let attempts = 0;
    const tick = () => {
      remeasure();
      attempts++;
      if (attempts < 20 && !document.querySelector(step.target)) {
        measureTimer.current = setTimeout(tick, 150);
      }
    };
    tick();
    return () => { if (measureTimer.current) clearTimeout(measureTimer.current); };
  }, [stepIdx, visible, remeasure, step]);

  useEffect(() => {
    if (!visible) return;
    const onChange = () => remeasure();
    window.addEventListener('resize', onChange);
    window.addEventListener('scroll', onChange, true);
    return () => {
      window.removeEventListener('resize', onChange);
      window.removeEventListener('scroll', onChange, true);
    };
  }, [remeasure, visible]);

  useEffect(() => {
    if (!tipRef.current) return;
    setTipW(tipRef.current.offsetWidth);
    setTipH(tipRef.current.offsetHeight);
  }, [stepIdx, visible]);

  useEffect(() => {
    if (!visible) return;
    const onKey = (e) => { if (e.key === 'Escape') close(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [visible]); // eslint-disable-line react-hooks/exhaustive-deps

  const next = () => {
    if (isLast) { close(); return; }
    setStepIdx(stepIdx + 1);
  };
  const back = () => { if (!isFirst) setStepIdx(stepIdx - 1); };
  const close = () => { setVisible(false); setStepIdx(0); setSoloStepId(null); };
  const startTour = () => { setSoloStepId(null); setStepIdx(0); setVisible(true); };

  // Sidebar "Replay Tour" dispatches this event after clearing the
  // per-page seen flags. We start the tour for whichever page the user
  // is on, so they don't have to navigate to find where to retry.
  useEffect(() => {
    if (!tourKey) return;
    const onReplay = () => startTour();
    window.addEventListener('scn-replay-tour', onReplay);
    return () => window.removeEventListener('scn-replay-tour', onReplay);
  }, [tourKey]);

  // The AI assistant dispatches this after navigating to the tour's page, it
  // opens the tour at one specific step, in solo mode (just that spotlight).
  useEffect(() => {
    const onGoto = (e) => {
      const d = (e && e.detail) || {};
      if (!tourKey || !steps || d.tourKey !== tourKey) return;
      if (!steps.some(s => s.id === d.stepId)) return;
      setSoloStepId(d.stepId);
      setStepIdx(0);
      setVisible(true);
    };
    window.addEventListener('scn-tour-goto', onGoto);
    return () => window.removeEventListener('scn-tour-goto', onGoto);
  }, [tourKey, steps]);

  // Tell the AI assistant whether this page has a tour, so it only offers
  // "Show me around this page" when there's actually one to run.
  useEffect(() => {
    window.__scnHasPageTour = !!(tourKey && steps);
    return () => { window.__scnHasPageTour = false; };
  }, [tourKey, steps]);

  if (!tourKey || !steps) return null;

  const pos = computeTooltipPos(rect, tipW, tipH);

  return (
    <>
      {/* The old floating "?" button was removed, it sat under the AI chat
          bubble (both bottom-right) and was hidden on desktop. The page tour is
          now launched from the AI assistant ("Show me around this page") and the
          sidebar "Replay Tour", both of which dispatch the scn-replay-tour event
          handled above. */}

      {visible && step && createPortal(
        <div style={{ position: 'fixed', inset: 0, zIndex: 9998, pointerEvents: 'none' }}>
          {/* Dim overlay, soft, doesn't fully block clicks behind the spot. */}
          <div style={{
            position: 'absolute', inset: 0, background: 'rgba(13,27,62,0.32)',
            pointerEvents: 'auto', cursor: 'pointer',
          }} onClick={close} />

          {/* Spotlight ring around target if we have one */}
          {rect && (
            <div style={{
              position: 'absolute',
              top: rect.top - 6, left: rect.left - 6,
              width: rect.width + 12, height: rect.height + 12,
              borderRadius: 10,
              boxShadow: '0 0 0 9999px rgba(13,27,62,0.32), 0 0 0 3px rgba(201,162,39,0.9)',
              pointerEvents: 'none',
              transition: 'all 0.2s ease',
            }} />
          )}

          {/* Tooltip */}
          <div ref={tipRef} style={{
            position: 'absolute',
            top: pos.top, left: pos.left,
            width: 340, maxWidth: 'calc(100vw - 32px)',
            background: '#fff', borderRadius: 12,
            boxShadow: '0 20px 50px -8px rgba(13,27,62,0.4), 0 8px 20px -4px rgba(13,27,62,0.2)',
            padding: '18px 20px',
            pointerEvents: 'auto',
            fontFamily: "'Manrope', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
            border: '1px solid #e5e7eb',
          }}>
            <button onClick={close} aria-label="Close tour" style={{
              position: 'absolute', top: 10, right: 10,
              background: 'none', border: 'none', cursor: 'pointer',
              color: '#9aa1ad', padding: 4, lineHeight: 0,
            }}><X size={16} /></button>

            <div style={{ fontSize: 10, fontWeight: 800, color: GOLD, letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: 6 }}>
              Step {stepIdx + 1} of {total}
            </div>
            <div style={{ fontSize: 16, fontWeight: 800, color: NAVY, letterSpacing: '-0.3px', marginBottom: 8, paddingRight: 18 }}>
              {step.title}
            </div>
            <div style={{ fontSize: 13, color: '#4a5568', lineHeight: 1.55, marginBottom: 14 }}>
              {step.content}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10 }}>
              <button onClick={back} disabled={isFirst} style={{
                background: 'none', border: 'none', cursor: isFirst ? 'default' : 'pointer',
                color: isFirst ? '#cbd5e1' : '#6b7280',
                fontSize: 13, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 4, padding: 4,
                fontFamily: 'inherit',
              }}><ChevronLeft size={14} /> Back</button>
              <button onClick={next} style={{
                background: NAVY, color: '#fff', border: 'none',
                padding: '8px 16px', borderRadius: 7, cursor: 'pointer',
                fontSize: 13, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 4,
                fontFamily: 'inherit',
              }}>
                {isLast ? 'Got it' : 'Next'}
                {!isLast && <ChevronRight size={14} />}
              </button>
            </div>
          </div>
        </div>,
        document.body,
      )}
    </>
  );
}
